import {
  Grid,
  Heading,
  Box,
  Flex,
  Stack,
  Text,
  Input,
  IconButton,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  PopoverArrow,
  Portal,
  useBreakpointValue,
  useColorModeValue,
} from '@chakra-ui/react';
import {
  Cell,
  Column,
  ColumnInstance,
  HeaderGroup,
  Row,
  useFilters,
  useSortBy,
  useTable,
} from 'react-table';
import React, { ReactNode, useEffect } from 'react';
import { ArrowDownIcon, ArrowUpIcon, UpDownIcon } from '@chakra-ui/icons';
import { FiFilter } from 'react-icons/fi';
import { useHistory } from 'react-router-dom';
import EmptyState from './EmptyState';

interface GridInstanceProps {
  data: {}[];
  columns: Column[];
  /** row's document URL — a single click on the row navigates to it in place */
  rowHref?: (original: any) => string;
  /** show per-column text-filter icons in the header (item 86; doc grids opt in) */
  enableColumnFilters?: boolean;
  /** reports the visible (post column-filter) row count so the shell's "N records" stays truthful */
  onRowCountChange?: (n: number) => void;
  /** item 322d — empty-state copy when there's no data at all (filtered-to-zero has its own) */
  emptyHeading?: string;
  emptySubtext?: string;
}

// Wrap every plain cell value in a <span> so React always removes an ELEMENT it
// created, never a bare text node. Browser extensions (crypto wallets, translate)
// wrap/move text nodes in the page; if React is tracking the raw text node it then
// can't remove it ("removeChild: not a child") and the screen crashes. With the
// span, the extension's wrapping happens harmlessly INSIDE a node React owns.
// Used as react-table's defaultColumn.Cell (columns with their own Cell keep it).
const DEFAULT_COLUMN = {
  Cell: ({ value }: { value: any }) => (
    <span>{value === null || value === undefined ? '' : value}</span>
  ),
};

// sort affordance: a bright arrow when the column is sorted, else a dim up/down
// hint so it's obvious the header is clickable-to-sort (item 86)
const SortCaret: React.FC<{ column: ColumnInstance }> = ({ column }) => {
  if (column.isSorted) return column.isSortedDesc ? <ArrowDownIcon /> : <ArrowUpIcon />;
  return <UpDownIcon boxSize="0.7em" opacity={0.4} />;
};

// a header text-contains filter (item 86) — popover with an input, driven by
// react-table's per-column setFilter; the icon lights up when a filter is active
const ColumnFilter: React.FC<{ column: ColumnInstance }> = ({ column }) => {
  const active = Boolean(column.filterValue);
  return (
    <Popover placement="bottom-start" isLazy>
      <PopoverTrigger>
        <IconButton
          aria-label={`Filter ${String(column.id)}`}
          icon={<FiFilter />}
          size="xs"
          variant="ghost"
          minW="auto"
          h="auto"
          p={1}
          color={active ? 'brandGreen.300' : 'whiteAlpha.500'}
          _hover={{ color: 'white', bg: 'whiteAlpha.200' }}
          onClick={(e) => e.stopPropagation()}
        />
      </PopoverTrigger>
      <Portal>
        <PopoverContent w="220px">
          <PopoverArrow />
          <PopoverBody>
            <Input
              size="sm"
              autoFocus
              placeholder="Filter…"
              value={(column.filterValue as string) ?? ''}
              onChange={(e) => column.setFilter(e.target.value || undefined)}
            />
          </PopoverBody>
        </PopoverContent>
      </Portal>
    </Popover>
  );
};

// interactive content inside a cell (menus, selects, links, the kebab) shouldn't
// trigger the row's navigate-on-click — leave those clicks alone.
const isInteractive = (e: React.SyntheticEvent) => Boolean(
  (e.target as HTMLElement).closest('button, a, select, input, [role="menu"], [role="menuitem"]'),
);

// A clickable cell/card that opens its row's document on a single click (item 90
// item 367 — per-grid sort persistence. Identity = the column accessor list (stable
// per grid shape), value = react-table's sortBy array.
const sortStorageKey = (columns: readonly any[]): string => `basix.sort:${columns.map((c) => String(c?.accessor ?? '')).join(',')}`;

const readSavedSort = (columns: readonly any[]): { id: string; desc?: boolean }[] => {
  try {
    const raw = JSON.parse(window.localStorage.getItem(sortStorageKey(columns)) ?? '[]');
    return Array.isArray(raw) ? raw.filter((x) => x && typeof x.id === 'string') : [];
  } catch {
    return [];
  }
};

// scoped open-in-new-tab to sidebar nav only, so grid rows just navigate in place;
// no long-press / double-tap gesture anymore). Clicks on interactive content
// (menus, inputs, the kebab) are left alone.
const ClickCell: React.FC<{ href: string, children: ReactNode } & Record<string, any>> = ({
  href, children, ...rest
}) => {
  const history = useHistory();
  return (
    <Box
      {...rest}
      cursor="pointer"
      onClick={(e: React.MouseEvent) => {
        if (isInteractive(e)) return;
        history.push(href);
      }}
    >
      {children}
    </Box>
  );
};

const sortDirection = (column: ColumnInstance): 'ascending' | 'descending' | 'none' => {
  if (!column.isSorted) return 'none';
  return column.isSortedDesc ? 'descending' : 'ascending';
};

// One row rendered as a stacked, labeled card (narrow screens). Each cell sits
// under its column header as a label; columns with no header (status icon,
// actions menu) render label-less so they read as the card's lead/trailing bits.
const RowCard: React.FC<{ row: Row, href?: string }> = ({ row, href }) => {
  const cardBg = useColorModeValue('gray.50', 'whiteAlpha.50');
  const cardBorder = useColorModeValue('gray.200', 'whiteAlpha.100');
  const labelColor = useColorModeValue('gray.500', 'whiteAlpha.600');

  // item 534 — voided/archived rows stay in the ledger but read as marked (dimmed; struck if void)
  const orig = row.original as { isVoid?: boolean; isArchive?: boolean };
  const dimmed = Boolean(orig.isVoid || orig.isArchive || (orig as any).isArchived);
  const cardProps = {
    role: 'listitem' as const,
    bg: cardBg,
    borderWidth: '1px',
    borderColor: cardBorder,
    borderRadius: 'lg',
    p: 4,
    opacity: dimmed ? 0.5 : undefined,
    textDecoration: orig.isVoid ? 'line-through' : undefined,
    transition: 'border-color 0.15s, transform 0.15s',
    ...(href ? { cursor: 'pointer', _hover: { borderColor: 'brandGreen.400' }, _active: { transform: 'scale(0.995)' } } : {}),
  };

  // status + actions/kebab are pinned to a header row (status left, kebab right);
  // every grid shares these accessors ('status' / 'id'). The rest stack below as
  // labeled fields. Grids without a status column just show the kebab.
  const statusCell = row.cells.find((cell: Cell) => cell.column.id === 'status');
  const actionsCell = row.cells.find((cell: Cell) => cell.column.id === 'id');
  const bodyCells = row.cells.filter(
    (cell: Cell) => cell.column.id !== 'status' && cell.column.id !== 'id',
  );
  const hasHeaderRow = Boolean(statusCell || actionsCell);

  const inner = (
    <Stack spacing={3}>
      {hasHeaderRow && (
        <Flex
          align="center"
          justify="space-between"
          gap={2}
          pb={bodyCells.length ? 3 : 0}
          borderBottomWidth={bodyCells.length ? '1px' : 0}
          borderBottomColor={cardBorder}
        >
          <Box minW={0}>{statusCell ? statusCell.render('Cell') : null}</Box>
          <Box flexShrink={0}>{actionsCell ? actionsCell.render('Cell') : null}</Box>
        </Flex>
      )}
      {bodyCells.map((cell: Cell) => {
        const hasLabel = Boolean(cell.column.Header);
        return (
          <Box key={`mcell:${cell.column.id}${cell.row.id}`}>
            {hasLabel && (
              <Text
                fontSize="0.65rem"
                fontWeight="bold"
                letterSpacing="0.1em"
                textTransform="uppercase"
                color={labelColor}
                mb={1}
              >
                {cell.column.render('Header')}
              </Text>
            )}
            <Box fontSize="sm">{cell.render('Cell')}</Box>
          </Box>
        );
      })}
    </Stack>
  );

  return href
    ? <ClickCell href={href} {...cardProps}>{inner}</ClickCell>
    : <Box {...cardProps}>{inner}</Box>;
};

RowCard.defaultProps = { href: undefined };

const GridInstance: React.FC<GridInstanceProps> = ({
  columns, data, rowHref, enableColumnFilters, onRowCountChange, emptyHeading, emptySubtext,
}) => {
  const {
    headerGroups,
    rows,
    prepareRow,
    state,
  } = useTable(
    {
      columns,
      data,
      defaultColumn: DEFAULT_COLUMN,
      initialState: {
        hiddenColumns: columns
          .filter((col: any) => col.show === false)
          .map((col) => col.accessor) as any,
        // item 367 — restore this grid's saved sort (see sortStorageKey below)
        sortBy: readSavedSort(columns),
      },
    },
    useFilters,
    useSortBy,
  );
  useEffect(() => { onRowCountChange?.(rows.length); }, [rows.length, onRowCountChange]);
  // item 367 — clicking a header to sort PERSISTS per grid, per device (Chad: "when I
  // reorder a column anywhere it should always stay"). The grid's identity is its
  // column-accessor list, so the same grid keeps its sort across pages and sessions.
  const {sortBy} = state as any;
  useEffect(() => {
    try {
      window.localStorage.setItem(sortStorageKey(columns), JSON.stringify(sortBy ?? []));
    } catch { /* no storage */ }
  }, [sortBy, columns]);
  const rowBorder = useColorModeValue('gray.100', 'whiteAlpha.100');
  // item 322i — whole-row hover; the old per-cell hover was invisible in light mode
  const rowHoverBg = useColorModeValue('blackAlpha.50', 'whiteAlpha.100');
  // phones + tablet-portrait get stacked cards; laptops/desktops get the table.
  // `lg` (992px) is the line — below it a multi-column table would only
  // horizontal-scroll, which is the cramped experience we're replacing.
  const asCards = useBreakpointValue({ base: true, lg: false }) ?? false;

  // item 322d — an empty grid used to render NOTHING (looked identical to "still
  // loading"). Say it plainly; distinguish truly-no-data from filtered-to-zero.
  if (rows.length === 0) {
    if (data.length > 0) {
      return (
        <EmptyState
          icon={<FiFilter />}
          heading="No matches"
          subtext={`Your column filters hide all ${data.length} row${data.length === 1 ? '' : 's'} — clear them to see everything.`}
        />
      );
    }
    return <EmptyState heading={emptyHeading} subtext={emptySubtext} />;
  }

  if (asCards) {
    return (
      <Stack role="list" spacing={3}>
        {rows.map((row) => {
          prepareRow(row);
          const href = rowHref ? rowHref(row.original) : undefined;
          return <RowCard key={`rowcard:${row.id}`} row={row} href={href} />;
        })}
      </Stack>
    );
  }

  // Every column always renders — wide windows show the full table; if it still
  // overflows the container it scrolls horizontally rather than dropping data.
  // item 338/363 — per-column tracks: slim ACTION columns (the kebab 'id' + the pin
  // 'isFavorite') get 3rem instead of a full data share (they were as wide as NAME,
  // shoving real content to the middle of the page); a column def can also set its own
  // `gridWidth` (e.g. NAME 'minmax(16rem, 2fr)') for page-specific emphasis.
  const trackFor = (c: any): string => {
    if (c?.gridWidth) return String(c.gridWidth);
    const accessor = String(c?.accessor ?? '');
    if (accessor === 'id' || accessor === 'isFavorite') return '3rem';
    return 'minmax(8rem, 1fr)';
  };
  const templateColumns = columns.map(trackFor).join(' ');
  return (
    <Box overflowX="auto" role="table">
      <Grid templateColumns={templateColumns}>
        {headerGroups.map((headerGroup: HeaderGroup) => headerGroup.headers.map((column: ColumnInstance, i: number) => (
          // dark-glass header band (item 46) — always dark, letterspaced
          <Heading
            role="columnheader"
            aria-sort={sortDirection(column)}
            {...column.getHeaderProps()}
            size="xs"
            key={`heading:${column.id}`}
            p={2}
            height={8}
            color="whiteAlpha.900"
            bgGradient="linear(to-b, gray.800, gray.900)"
            letterSpacing="0.1em"
            borderLeftRadius={i === 0 ? 'md' : undefined}
            borderRightRadius={i === headerGroup.headers.length - 1 ? 'md' : undefined}
            alignSelf="center"
            style={{ whiteSpace: 'nowrap' }}
          >
            <Flex
              align="center"
              justify={(column as any).align === 'right' ? 'flex-end' : 'space-between'}
              gap={1}
            >
              {column.id === 'id' ? (
                <Box>{column.render('Header')}</Box>
              ) : (
                <Flex
                  align="center"
                  gap={1}
                  cursor="pointer"
                  _hover={{ color: 'white' }}
                  {...column.getSortByToggleProps({ title: 'Sort' })}
                >
                  <Box>{column.render('Header')}</Box>
                  <SortCaret column={column} />
                </Flex>
              )}
              {enableColumnFilters && column.canFilter && column.id !== 'id' && column.id !== 'status' && (
                <ColumnFilter column={column} />
              )}
            </Flex>
          </Heading>
        )))}

        {rows.map((row) => {
          prepareRow(row);
          const href = rowHref ? rowHref(row.original) : undefined;
          // item 534 — voided/archived rows STAY in the ledger but read as marked:
          // dimmed (both) + struck through (void). display:contents can't carry visual
          // styles, so apply per-cell.
          const orig = row.original as { isVoid?: boolean; isArchive?: boolean };
          const dimmed = Boolean(orig.isVoid || orig.isArchive || (orig as any).isArchived);
          return (
            // item 322i — a display:contents wrapper gives the flat grid cells a real
            // ROW parent: CSS-only whole-row hover (no re-render), row semantics for
            // screen readers, and a visible "this row is clickable" affordance.
            <Box
              key={`row:${row.id}`}
              display="contents"
              role="row"
              sx={{ '&:hover > *': { bg: rowHoverBg } }}
            >
              {row.cells.map((cell: Cell) => {
                const cellProps = {
                  role: 'cell',
                  fontSize: 'xs',
                  px: 2,
                  py: 3,
                  key: `cell:${cell.column.id}${cell.row.id}`,
                  borderBottomWidth: '1px',
                  borderBottomColor: rowBorder,
                  opacity: dimmed ? 0.5 : undefined,
                  textDecoration: orig.isVoid ? 'line-through' : undefined,
                  display: 'flex',
                  alignItems: 'center',
                  // item 322i — money/number columns opt into right alignment via a
                  // custom `align: 'right'` on the column def
                  justifyContent: (cell.column as any).align === 'right' ? 'flex-end' : undefined,
                  transition: 'background 0.12s ease',
                  // let cells shrink to their grid track so wide content (e.g. status
                  // pills) wraps within the column instead of overflowing the next
                  minW: 0,
                };
                return href
                  ? <ClickCell href={href} {...cellProps}>{cell.render('Cell')}</ClickCell>
                  : <Box {...cellProps}>{cell.render('Cell')}</Box>;
              })}
            </Box>
          );
        })}
      </Grid>
    </Box>
  );
};
GridInstance.defaultProps = {
  rowHref: undefined,
  enableColumnFilters: false,
  onRowCountChange: undefined,
  emptyHeading: undefined,
  emptySubtext: undefined,
};

export default GridInstance;
